import { Component, DestroyRef, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ComponentHotkeyService } from '@shared/component-hotkey.service';
import { ProjectNavigationModel, ProjectNavigationSorting, ProjectNavigationSortingType, ProjectNavigationTab } from './project-navigation.model';
import { ProjectNavigationService } from './service/project-navigation.service';
import { ProjectGroupUtils } from '@project/components/project/util/project-group-utils';
import { ProjectModel } from '@project/model/project.model';
import { ProjectHeadVO } from '@project/model/vo/project.vo';
import { ProjectGroupVO } from '@project/model/vo/project-group.vo';
import { UserModel } from '@user/model/user.model';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ProjectUtils } from '@project/components/project/util/project-utils';
import { ArticleUsageVO } from '@article/model/vo/article.vo';
import { SelectionModel } from '@project/components/article/model/selection.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ProjectNavigationItemActionsService } from './service/project-navigation-item-actions.service';
import { IsProjectType } from '@project/model/is-project-type';

@Component({
  standalone: false,
  selector: 'project-navigation',
  providers: [ComponentHotkeyService],
  templateUrl: './project-navigation.component.html',
  styleUrls: ['./project-navigation.component.scss'],
  host: {class: "d-flex flex-column px-2 flex-grow-1"},
})
export class ProjectNavigationComponent implements OnInit, OnDestroy {

  public readonly projectNavigationSortingType = ProjectNavigationSortingType;

  public readonly Tab = ProjectNavigationTab;

  public allSelectedState: "all" | "some" | "none" = "none";

  public showHiddenItems: boolean = false;
  public currentSortingState: ProjectNavigationSorting = {
    type: ProjectNavigationSortingType.NAME,
    ascending: true,
  };

  private readonly specialName = `myAssignedProjects.groupName.label`;
  public loadingTab = false;

  @Input({required: true}) model!: ProjectNavigationModel;
  @Input({required: true}) headerTitle!: string;
  @Input({required: true}) headerText!: string;
  @Input() headerHelpLink?: string;

  @Input({required: true}) visibleFeatures!: VisibleFeatures;

  @Input() multipleSelectionWithinSingleProject: boolean = false;
  @Input() actionButtonLabel?: string;
  @Input() selectionAvailable: boolean = false;
  @Input() compositeCheckBoxLabel?: string;
  @Input() isNewProjectCreation: boolean = false;
  @Input() itemToMoveOrCopy?: ProjectGroupVO | ProjectHeadVO;
  @Input() submitEnabled: boolean = false;
  @Input() whySubmitDisabled: string = "";

  @Output() submitEnabledChanged = new EventEmitter<void>();
  @Output() actionBtnClick = new EventEmitter<void>();
  @Output() submitSelectedConfiguration = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();
  @Output() componentInitialized = new EventEmitter<void>();

  public constructor(
    private readonly destroyRef: DestroyRef,
    private readonly projectNavigationService: ProjectNavigationService,
    private readonly projectModel: ProjectModel,
    public readonly userModel: UserModel,
    private readonly selectionModel: SelectionModel,
    private readonly localeResourceManager: LocaleResourceManager,
    public readonly actionsService: ProjectNavigationItemActionsService,
    private readonly componentHotkeyService: ComponentHotkeyService,
  ) {

    this.actionsService.redrawList$
      .pipe(takeUntilDestroyed()).subscribe(() => {
      this.sortCurrentProjectGroup();
      this.updateViewItems();
    });

    this.addHotkeys();
  }

  public async ngOnInit() {
    await this.projectNavigationService.initializeProjectNavigationData(this.model);
    this.model.visiblePinnedProjects = this.visibleFeatures.pinnedProjects;

    this.initNewConfigNameIfNecessary();
    this.updateSubmitEnabled();
    this.model.currentChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((group: ProjectGroupVO) => this.onCurrenGroupChanged(group));
    this.componentInitialized.emit();

    this.model.currentSelectedItemObserver.subscribe(() => this.updateSubmitEnabled());
  }

  private onCurrenGroupChanged(currentGroup: ProjectGroupVO): void {
    if (!currentGroup) {
      return;
    }
    this.sortCurrentProjectGroup();
    this.updateViewItems();
    this.model.scrollToTop.next();
  }

  public isNewProject(): boolean {
    return this.projectModel.currentProject && this.projectModel.isNewProject;
  }

  public async openTabCurrentUser(): Promise<void> {
    if(this.model.currentTab == ProjectNavigationTab.Own) return;
    if (this.model.compositeCheckBoxValue) {
      this.projectNavigationService.openProjectGroup(this.model, this.model.rootProjectGroup.groups[0]);
      return;
    }
    await this.projectNavigationService.openPreselectedProjectGroupInMyOwnTab(this.model);
  }

  public async openTabAssignedProjects(): Promise<void> {
    if(this.model.currentTab == ProjectNavigationTab.MyAssigned) return;
    await this.openTabFn(() => this.projectNavigationService.openMyAssignedProjects(this.model));
  }

  public async openTabRecentlyUsedProjects(): Promise<void> {
    if(this.model.currentTab == ProjectNavigationTab.RecentlyUsed) return;
    await this.openTabFn(() => this.projectNavigationService.openRecentlyUsedProjects(this.model));
  }

  public async openTabPinnedProjects(): Promise<void> {
    if(this.model.currentTab == ProjectNavigationTab.Pinned) return;
    await this.openTabFn(() => this.projectNavigationService.openPinnedProjects(this.model));
  }

  public async openTabSharedUser(): Promise<void> {
    if(this.model.currentTab == ProjectNavigationTab.Shared) return;
    await this.openTabFn(() => this.projectNavigationService.openSharedUser(this.model));
  }

  private async openTabFn(openFn: () => Promise<void>): Promise<void> {
    this.loadingTab = true;
    try {
      await openFn();

    } finally {
      this.loadingTab = false;
    }
  }

  public async openTabSearch(): Promise<void> {
    await this.projectNavigationService.openSearch(this.model);
    this.projectNavigationService.triggerFilterBySearch();
    this.updateViewItems();
  }

  public async navigateToSearchRoot() {
    this.model.clearCurrentViewedProjectGroup();
    await this.projectNavigationService.openGroup(this.model, this.model.rootSearchProjectGroup);
    this.sortCurrentProjectGroup();
    this.updateViewItems();
  }

  public isAddProjectGroupDisabled(): boolean {
    return this.projectNavigationService.isAddProjectGroupDisabled(this.model);
  }

  public newFolder(): void {
    this.projectNavigationService.newFolder(this.model);
  }

  public async newProjectGroup(): Promise<void> {
    await this.projectNavigationService.newProjectGroup(this.model);
  }

  public isNewConfigurationGroupButtonVisible(): boolean {
    return (
      this.visibleFeatures.newFolderBtn &&
      (!this.projectModel.isNewConfigurationGroup || this.isNewProjectCreation) &&
      this.visibleFeatures.newConfigurationGroupBtn
    );
  }

  public updateSubmitEnabled() {
    this.updateAllSelectedState();
    this.submitEnabledChanged.emit();
  }

  public async navigateInChain(projectGroup: ProjectGroupVO): Promise<void> {
    if (!projectGroup) {
      return;
    }

    const currentViewedProjectGroupIndex = this.model.currentProjectFolder.groups.findIndex(e => e.id == this.model.currentViewedProjectGroup?.id);
    const followingGroupInChain = this.projectNavigationService.findFollowingGroupInChain(this.model, projectGroup, currentViewedProjectGroupIndex);

    this.model.clearCurrentViewedProjectGroup();

    const tabHasNoNestedPaths = [ProjectNavigationTab.Pinned, ProjectNavigationTab.RecentlyUsed].includes(this.model.currentTab);
    if (tabHasNoNestedPaths) {
      return;
    }

    await this.projectNavigationService.openGroup(this.model, projectGroup);
    this.sortCurrentProjectGroup();
    this.updateViewItems();

    if(followingGroupInChain) {
      this.model.selectItem(followingGroupInChain, this.projectModel)
      this.model.scrollToSelected.next();
    }
  }

  public getBreadcrumbLabel(projectGroup: ProjectGroupVO, index: number): string | undefined {
    if (projectGroup.groupName === this.specialName) {
      return projectGroup.groupOwner.userId === this.userModel.user.id
        ? this.localeResourceManager.getValueByKey('owner.myAssignedProjects.groupName.label')
        : this.localeResourceManager.getValueByKey('myAssignedProjects.groupName.label', {'0': projectGroup.groupOwner.username});
    } else if (index === 0) {
      return this.localeResourceManager.getValueByKey(projectGroup.groupName, undefined, true);
    } else {
      return projectGroup.groupName;
    }
  }

  public isSortingAvailable(): boolean {
    return this.model.currentTab !== ProjectNavigationTab.RecentlyUsed;
  }

  public onSortingChanged(sortingType: ProjectNavigationSortingType): void {
    if (!this.isSortingAvailable()) {
      return;
    }
    if (this.currentSortingState.type === sortingType) {
      return;
    } else {
      this.currentSortingState.type = sortingType;
    }
    this.sortCurrentProjectGroup();
    this.updateViewItems();
  }

  public switchSortOrder() {
    this.currentSortingState.ascending = !this.currentSortingState.ascending;
    this.sortCurrentProjectGroup();
    this.updateViewItems();
  }

  private sortCurrentProjectGroup(): void {
    if (!this.isSortingAvailable()) {
      return;
    }
    this.sortCurrentProjectGroupByType(this.currentSortingState);
  }

  public async performSearch(searchString: string): Promise<void> {
    await this.projectNavigationService.performSearch(this.model, searchString, this.model.toggleExtendedSearch);
    this.model.clearCurrentViewedProjectGroup();
  }

  public updateViewItems(): void {
    const currentGroup: ProjectGroupVO = this.model.currentProjectFolder;
    const viewItems: (ProjectGroupVO | ProjectHeadVO)[] = [];
    if (!currentGroup) {
      this.model.viewItems = [];
      return;
    }
    for (const group of currentGroup.groups) {
      if(!group.isHidden || this.showHiddenItems) {
        viewItems.push(group);
      }
      if (this.model.searchMode) {
        viewItems.push(...group.projects);
      }
    }
    this.model.viewItems = [...viewItems, ...currentGroup.projects];
    this.updateSubmitEnabled();
  }

  public toggleShowHiddenItems() {
    this.showHiddenItems = !this.showHiddenItems
    this.updateViewItems()
  }

  public sortCurrentProjectGroupByType(sorting: ProjectNavigationSorting): void {
    const currentProjectGroup: ProjectGroupVO = this.model.searchMode ? this.model.rootSearchProjectGroup : this.model.currentProjectFolder;
    if (!currentProjectGroup) {
      return;
    }
    this.sortGroups(sorting, currentProjectGroup);
    this.sortProjects(sorting, currentProjectGroup);
  }

  private sortGroups(sorting: ProjectNavigationSorting, currentProjectGroup: ProjectGroupVO): void {
    const ascending: boolean = sorting.type === ProjectNavigationSortingType.NAME ? sorting.ascending : true;
    currentProjectGroup.groups.sort((a: ProjectGroupVO, b: ProjectGroupVO) =>
      ProjectGroupUtils.projectGroupSortFunction(a, b, this.userModel.user.id, ascending)
    );
  }

  public userCanReassignProjects(): boolean {
    return this.userModel.user.hasPermission("CHANGE_PROJECT_OWNER_FOR_OWN_PROJECTS") ||
      this.userModel.user.hasPermission("CHANGE_PROJECT_OWNER_FOR_ALL_PROJECTS");
  }

  private sortProjects(sorting: ProjectNavigationSorting, currentProjectGroup: ProjectGroupVO): void {
    for (const group of currentProjectGroup.groups) {
      switch (sorting.type) {
        case ProjectNavigationSortingType.NAME:
          group.projects?.sort((a: ProjectHeadVO, b: ProjectHeadVO) => ProjectUtils.projectSortByNameFunction(a, b, sorting.ascending));
          break;
        case ProjectNavigationSortingType.ARTICLE_NUMBER:
          group.projects?.sort((a: ProjectHeadVO, b: ProjectHeadVO) => ProjectUtils.projectSortByArticleNumberFunction(a, b, sorting.ascending));
          break;
        case ProjectNavigationSortingType.DATE:
          group.projects?.sort((a: ProjectHeadVO, b: ProjectHeadVO) => ProjectUtils.projectSortByModificationDateFunction(a, b, sorting.ascending));
          break;
        case ProjectNavigationSortingType.STATUS:
          group.projects?.sort((a: ProjectHeadVO, b: ProjectHeadVO) => ProjectUtils.projectSortByLastHistoryActionFunction(a, b, sorting.ascending));
          break;
      }
    }
  }

  public async openFolder(projectGroup: ProjectGroupVO): Promise<void> {
    await this.projectNavigationService.openProjectGroup(this.model, projectGroup);
    this.updateSubmitEnabled();
  }

  public async openProject(project: ProjectGroupVO): Promise<void> {
    if(!this.model.canNavigateIntoProjectGroups) return;
    await this.projectNavigationService.openProjectGroup(this.model, project);
    this.model.setCurrentViewedProjectGroup(project);
    this.updateAllSelectedState();
    this.updateSubmitEnabled();
  }

  public onCancel(): void {
    this.cancel.emit();
  }

  public compositeCheckBoxChanged(): void {
    this.model.searchMode = false;
    this.resetAllData();
    this.initNewConfigName();
    this.projectNavigationService.openPreselectedProjectGroup(this.model);
    this.updateSubmitEnabled();
    this.projectNavigationService.triggerCompositeCheckBoxChanged();
  }

  private resetAllData(): void {
    this.model.resetAllData();
  }

  private initNewConfigName(): void {
    this.model.newConfigName = '';
    if (
      this.projectModel.isNewConfigurationGroup &&
      !ProjectUtils.isNewWSPImportedProject(this.projectModel.currentProject) &&
      !this.model.compositeCheckBoxValue
    ) {
      const configurationGroupName: string = ProjectUtils.getConfigurationGroupName(this.projectModel.currentProject);
      this.model.newConfigName = configurationGroupName;
    } else if (
      this.projectModel.currentProject?.projectGroup?.id === -1
    ) {
      this.model.newConfigName = this.projectModel.currentProject.projectGroup.groupName;
    } else {
      if (this.projectModel.currentProject && this.projectModel.currentProject.name) {
        this.model.newConfigName = this.projectModel.currentProject.name;
      } else if (IsProjectType.mcsGds(this.projectModel.currentProject)) {
        const mcsGdsArticleNo: string = this.projectModel.currentInfoProperties.getString("mcsGdsInitArticleNo");
        const index: number = mcsGdsArticleNo === null ? -1 : mcsGdsArticleNo.indexOf('-');
        let mcsGdsSeries: string = '';
        if (index === -1) {
          mcsGdsSeries = mcsGdsArticleNo;
        } else if (mcsGdsArticleNo) {
          mcsGdsSeries = mcsGdsArticleNo.substr(0, index);
        }
        this.model.newConfigName = mcsGdsSeries;
      }
    }
    this.fillBaseArticleInformation(this.model);
  }

  public initNewConfigNameIfNecessary() {
    if (this.model.saveMode !== this.model.SAVE_AS_BUILDING_BLOCK) {
      this.initNewConfigName();
    }
  }

  private fillBaseArticleInformation(model: ProjectNavigationModel): void {
    const selectedUsageIds: string[] = this.selectionModel.getArticleUsageIds();
    if (selectedUsageIds.length === 1 && selectedUsageIds[0]) {
      const usageId: string = selectedUsageIds[0];
      const articleUsage: ArticleUsageVO = this.projectModel.getArticleUsage(usageId);
      if (articleUsage) {
        this.model.baseArticleId = articleUsage.articleId;
        this.model.baseArticleVersion = articleUsage.articleVersion;
        this.model.selectedBaseArticleUsageId = articleUsage.id;
      }
    }
  }

  private addHotkeys() {
    this.componentHotkeyService.add('enter', () => {
      if (this.submitEnabled) {
        this.actionBtnClick.emit();
      }
    });
    this.componentHotkeyService.add('up', () => this.moveSelectionUpDown(-1), {allowRepeat: true});
    this.componentHotkeyService.add('down', () => this.moveSelectionUpDown(1), {allowRepeat: true});
  }

  private moveSelectionUpDown(direction: 1 | -1): void {
    const items: (ProjectGroupVO | ProjectHeadVO)[] = this.model.currentViewedProjectGroup
      ? this.model.currentViewedProjectGroup.projects
      : this.model.viewItems;
    if (!items?.length) return;
    const currentIndex = items.indexOf(this.model.currentSelectedItem);
    let newIndex: number;
    if (currentIndex === -1) {
      newIndex = direction === 1 ? 0 : items.length - 1;
    } else {
      newIndex = Math.max(0, Math.min(items.length - 1, currentIndex + direction));
    }
    if (newIndex === currentIndex) return;
    this.model.selectItem(items[newIndex], this.projectModel);
  }

  public onActionBtnClick() {
    this.actionBtnClick.emit();
  }

  public openHELPLink(headerHelpLink: string) {
    window.open(this.localeResourceManager.getValueByKey(headerHelpLink), `_blank`);
  }

  public toggleSelectAllProjects(event: Event, projectGroup: ProjectGroupVO): void {
    event.preventDefault();
    const shouldSelectAll = this.allSelectedState == "none" || this.allSelectedState == "some";
    this.projectNavigationService
      .selectProjectGroup(this.model, projectGroup, shouldSelectAll, true);
    this.updateSubmitEnabled();
  }

  public updateAllSelectedState() {
    if (!this.selectionAvailable) return;
    this.allSelectedState = this.projectNavigationService
      .updateSelectionStateForProjectGroup(this.model, this.model.currentViewedProjectGroup);
  }

  public ngOnDestroy() {
    this.model.currentSelectedItemObserver.complete();
    this.model.searchMode = false;
    this.model.compositeCheckBoxValue = false;
  }

  protected readonly ProjectGroupVO = ProjectGroupVO;
  protected readonly ProjectNavigationModel = ProjectNavigationModel;
}

export type VisibleFeatures = {
  compositeCheckBox: boolean,
  pinnedProjects: boolean,
  recentlyUsedProjects: boolean,
  history: boolean,
  projectActionsMenu: boolean,
  projectGroupContextMenu: boolean,
  newConfigurationGroupBtn: boolean,
  newFolderBtn: boolean,
  dialogSearch: boolean,
  tabs: boolean,
  breadcrumbs: boolean,
}
